"use client"
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  VStack,
  Heading,
  Container,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Text,
  useColorModeValue,
  HStack,
  FormHelperText,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from "@chakra-ui/react"
import {
  ChevronRightIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline"
import { useLocation } from "react-router-dom"
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import {
  fetchJobDepartmentsAsync,
  selectJobDepartments,
} from "../../AdminPanel/JobPostingSlice/JobDepartment/JobDepartmentSlice"
import { fetchPublicJobTitlesAsync, selectJobTitles } from "../../AdminPanel/JobPostingSlice/JobTitles/JobTitlesSlice"
import {
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "../../employerDashboard/AboutYou/AboutSlice"
import {
  fetchProfileAsync,
  selectCompanyProfile,
} from "../../employerDashboard/employerDashAPIs/companyProfile/CompanyProfileSlice"
import { CurrencyRupeeIcon } from "@heroicons/react/24/solid"

// Convert HTML to plain text for validation
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html")
  return doc.body.textContent || ""
}

// Yup schema
const JobDetailsSchema = yup.object({
  hiring_company_name: yup
    .string()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name cannot exceed 100 characters"),
  job_title_uuid: yup.string().required("Job title is required"),
  job_name: yup
    .string()
    .required("Designation is required")
    .min(2, "Designation must be at least 2 characters")
    .max(100, "Designation cannot exceed 100 characters"),
  job_tag: yup.string().required("Please select a job level"),
  department_uuid: yup.string().required("Department is required"),
  job_type: yup
    .string()
    .oneOf(["Full Time", "Part Time", "Internship", "Freelance", "Temporary"], "Invalid job type")
    .required("Please select a job type"),
  work_location_type: yup
    .string()
    .oneOf(["On Site", "Remote", "Hybrid"], "Invalid location type")
    .required("Please select a location type"),
  job_shift: yup
    .string()
    .oneOf(["Day Shift", "Morning Shift", "Night Shift", "Mon - Fri", "UK Shift"], "Invalid shift")
    .required("Please select a shift type"),
  address1: yup
    .string()
    .required("Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(200, "Address cannot exceed 200 characters"),
  address2: yup.string().nullable().max(200, "Address cannot exceed 200 characters"),
  country_uuid: yup.string().required("Country is required"),
  state_uuid: yup.string().required("State is required"),
  city_uuid: yup.string().required("City is required"),
  pincode: yup
    .number()
    .typeError("Postal code is required")
    .required("Postal code is required")
    .test("len", "Please enter a valid 5-6 digit postal code", (val) => {
      if (typeof val !== "number") return false
      const str = String(val)
      return /^\d{5,6}$/.test(str)
    }),
  pay_type: yup.string().oneOf(["Do Not Disclose", "Salary Range"]).required(),
  salary_not_disclose: yup.boolean().required(),
  salary_min_amount: yup
    .string()
    .when("pay_type", {
      is: "Salary Range",
      then: (schema) =>
        schema
          .required("Minimum salary is required")
          .matches(/^[0-9]+$/, "Please enter a valid number")
          .test("min-gt-zero", "Salary must be greater than 0", (val) => Number(val) > 0),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  salary_max_amount: yup
    .string()
    .when("pay_type", {
      is: "Salary Range",
      then: (schema) =>
        schema
          .required("Maximum salary is required")
          .matches(/^[0-9]+$/, "Please enter a valid number")
          .test("gt-min", "Maximum salary must be greater than minimum salary", function (val) {
            const { salary_min_amount } = this.parent
            if (!salary_min_amount) return true
            return Number(val) > Number(salary_min_amount)
          }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  salary_per_type: yup.string().when("pay_type", {
    is: "Salary Range",
    then: (schema) => schema.required("Please select a salary period"),
    otherwise: (schema) => schema.notRequired(),
  }),
  additional_pay: yup.string().nullable().max(500, "Benefits description cannot exceed 500 characters"),
  joining_amount_required: yup.boolean().required(),
  joining_amount: yup.string().nullable().when("joining_amount_required", {
    is: true,
    then: (schema) =>
      schema
        .required("Joining amount is required")
        .matches(/^[0-9]+$/, "Please enter a valid number"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  no_of_hire: yup
    .string()
    .nullable()
    .test("number", "Please enter a valid number", (val) => !val || /^[0-9]+$/.test(val))
    .test("min1", "Number of openings must be at least 1", (val) => !val || Number(val) >= 1)
    .test("max999", "Number of openings cannot exceed 999", (val) => !val || Number(val) <= 999),
  job_description: yup
    .string()
    .required("Job description is required")
    .test("min-plain", "Job description must be at least 50 characters", (html) => {
      const text = stripHtml(html || "")
      return text.trim().length >= 50
    }),
  is_planned_start_date: yup.boolean().default(false),
  plan_start_date: yup.string().nullable().when("is_planned_start_date", {
    is: true,
    then: (schema) =>
      schema
        .required("Joining date is required")
        .test("future", "Joining date must be today or in the future", (value) => {
          if (!value) return false
          const selected = new Date(value)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          return selected >= today
        }),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
})

function JobDetails1() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const formBackground = useColorModeValue("white", "gray.700")
  const accentColor = useColorModeValue("blue.500", "blue.300")
  const errorColor = "red.500"

  // RHF setup with Yup
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
    getValues,
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(JobDetailsSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      job_type: "Full Time",
      work_location_type: "On Site",
      job_shift: "Day Shift",
      salary_per_type: "Monthly",
      joining_amount_required: false,
      job_title_uuid: "",
      hiring_company_name: "",
      pay_type: "Do Not Disclose",
      salary_not_disclose: true,
      is_planned_start_date: false,
    },
  })

  const countryList = useSelector(selectFetchCountry)
  const stateList = useSelector(selectFetchState)
  const citiesList = useSelector(selectFetchCities)
  const departments = useSelector(selectJobDepartments)
  const jobTitle = useSelector(selectJobTitles)
  const jobTitlesLoading = useSelector((state) => state.jobTitles.loading)
  const profileData = useSelector(selectCompanyProfile)

  const [selectedCountryId, setSelectedCountryId] = useState("")
  const [selectedStateId, setSelectedStateId] = useState("")
  const [isFirstSwitchOn, setIsFirstSwitchOn] = useState(false)
  const [payByOption, setPayByOption] = useState("Do Not Disclose")
  const [quillContent, setQuillContent] = useState("")
  const [jobTitleKeyword, setJobTitleKeyword] = useState("")
  const [selectedJobTitle, setSelectedJobTitle] = useState(null)
  const [selectedBenefits, setSelectedBenefits] = useState([])

  const watchSalaryMin = watch("salary_min_amount")
  const watchSalaryMax = watch("salary_max_amount")

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  )

  const formats = useMemo(
    () => ["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link"],
    []
  )

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Handle dependent selects
  const countryChange = (e) => {
    if (!e.target.value) return
    const countryId = JSON.parse(e.target.value)
    setSelectedCountryId(countryId.uuid)
    setValue("state_uuid", "", { shouldValidate: true })
    setValue("city_uuid", "", { shouldValidate: true })
    setSelectedStateId("")
    trigger("country_uuid")
  }

  const stateChange = (e) => {
    if (!e.target.value) return
    const stateId = JSON.parse(e.target.value)
    setSelectedStateId(stateId.uuid)
    setValue("city_uuid", "", { shouldValidate: true })
    trigger("state_uuid")
  }

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(fetchStateAsync(selectedCountryId))
    }
  }, [selectedCountryId, dispatch])

  useEffect(() => {
    if (selectedStateId) {
      dispatch(fetchCitiesAsync(selectedStateId))
    }
  }, [selectedStateId, dispatch])

  // Fetch initial lists
  useEffect(() => {
    dispatch(fetchJobDepartmentsAsync())
    dispatch(fetchPublicJobTitlesAsync())
    dispatch(fetchProfileAsync())
    if (!countryList || countryList.length === 0) {
      dispatch(fetchCountryAsync())
    }
  }, [dispatch]) // fetch once

  // Pre-fill company name from profile
  useEffect(() => {
    if (profileData?.company_name) {
      setValue("hiring_company_name", profileData.company_name, { shouldValidate: true })
    }
  }, [profileData?.company_name, setValue])

  // Debounced job title search
  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(fetchPublicJobTitlesAsync(jobTitleKeyword))
    }, 500)
    return () => clearTimeout(handler)
  }, [jobTitleKeyword, dispatch])

  // Keep hidden "additional_pay" in sync with Tag UI
  useEffect(() => {
    const joined = selectedBenefits.join(", ")
    setValue("additional_pay", joined, { shouldValidate: true })
  }, [selectedBenefits, setValue])

  // Fix persisted data restoration (supports both {formData: ...} and plain state)
  const persistedData = useMemo(() => {
    const s = location.state
    return s?.formData || s || null
  }, [location.state])

  useEffect(() => {
    if (persistedData) {
      // Reset all RHF values first
      reset(
        {
          job_type: "Full Time",
          work_location_type: "On Site",
          job_shift: "Day Shift",
          salary_per_type: "Monthly",
          joining_amount_required: false,
          job_title_uuid: "",
          hiring_company_name: "",
          pay_type: "Do Not Disclose",
          salary_not_disclose: true,
          is_planned_start_date: false,
          ...persistedData,
        },
        { keepErrors: false, keepDirty: false }
      )

      // Restore Quill content
      if (persistedData.job_description) {
        setQuillContent(persistedData.job_description)
      }

      // Restore job title selected display
      if (persistedData.job_title_uuid) {
        try {
          const jobTitleData = JSON.parse(persistedData.job_title_uuid)
          setSelectedJobTitle(jobTitleData)
        } catch (e) {
          console.error("Could not parse job title data", e)
        }
      }

      // Restore Country/State selections to trigger dependent fetch
      if (persistedData.country_uuid) {
        try {
          const countryData = JSON.parse(persistedData.country_uuid)
          setSelectedCountryId(countryData.uuid)
        } catch (e) {
          console.error("Could not parse country data", e)
        }
      }
      if (persistedData.state_uuid) {
        try {
          const stateData = JSON.parse(persistedData.state_uuid)
          setSelectedStateId(stateData.uuid)
        } catch (e) {
          console.error("Could not parse state data", e)
        }
      }

      // Restore pay type + salary option
      if (persistedData.pay_type) {
        setPayByOption(persistedData.pay_type)
      }

      // Restore planned date switch
      if (typeof persistedData.is_planned_start_date === "boolean") {
        setIsFirstSwitchOn(persistedData.is_planned_start_date)
      }

      // Restore benefits (state driven)
      if (persistedData.additional_pay) {
        const benefits = persistedData.additional_pay
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
        setSelectedBenefits(Array.from(new Set(benefits)))
      }

      // Trigger UI validations update
      setTimeout(() => trigger(), 0)
    }
  }, [persistedData, reset, trigger])

  const handleSelectChange = (e) => {
    const selectedOption = e.target.value
    setPayByOption(selectedOption)

    const isRange = selectedOption === "Salary Range"
    setValue("pay_type", isRange ? "Salary Range" : "Do Not Disclose", { shouldValidate: true })
    setValue("salary_not_disclose", !isRange, { shouldValidate: true })

    if (isRange) {
      setTimeout(() => {
        trigger("salary_min_amount")
        trigger("salary_max_amount")
      }, 0)
    } else {
      // Clear salary fields when not disclosed
      setValue("salary_min_amount", "", { shouldValidate: true })
      setValue("salary_max_amount", "", { shouldValidate: true })
      setValue("salary_per_type", "Monthly", { shouldValidate: true })
    }
  }

  const handleQuillChange = (content) => {
    setQuillContent(content)
    setValue("job_description", content, { shouldValidate: true })
    setTimeout(() => {
      trigger("job_description")
    }, 0)
  }

  const handleJobTitleSelect = (item) => {
    const payload = JSON.stringify({ uuid: item.uuid, title: item.title })
    setSelectedJobTitle(item)
    setValue("job_title_uuid", payload, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
    clearErrors("job_title_uuid")
  }

  const onSubmit = (data) => {
    data.job_description = quillContent

    // Normalize and dedupe additional benefits
    const rawBenefits = (getValues("additional_pay") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
    const uniqueBenefits = Array.from(new Set(rawBenefits))
    data.additional_pay = uniqueBenefits.join(", ")

    navigate("/employer-dashboard/post-job/additional", { state: data })
  }

  const onSubmitError = (formErrors) => {
    const firstErrorField = Object.keys(formErrors)[0]
    if (firstErrorField) {
      if (firstErrorField === "job_description") {
        const el = document.getElementById("job-description-editor")
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      } else {
        const el = document.querySelector(`[name="${firstErrorField}"]`)
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }
    }
  }

  return (
    <Box minH="100vh" py={12} className=" bg-mainBg">
      <Container maxW="6xl">
        <Box bg={formBackground} borderRadius="xl" shadow="xl" p={6} transition="all 0.3s">
          <HStack spacing={3} mb={8} justify="center">
            <Heading as="h2" size="xl" textAlign="center" color={accentColor}>
              Post Your Job
            </Heading>
          </HStack>

          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon />
            Fill in the details below to create your job posting. Required fields are marked with an asterisk (*).
          </Alert>

          <form onSubmit={handleSubmit(onSubmit, onSubmitError)} noValidate>
            <VStack spacing={6} align="stretch">
              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md">
                <Heading as="h3" size="md" mb={2}>
                  Basic Job Information
                </Heading>
                <Text color="gray.600">Start by entering the fundamental details about the position.</Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired isInvalid={!!errors.hiring_company_name}>
                  <FormLabel>Company name</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      {...register("hiring_company_name")}
                      pl={10}
                      placeholder="Enter your company name"
                      _focus={{ borderColor: accentColor }}
                      isReadOnly
                      name="hiring_company_name"
                    />
                  </InputGroup>
                  <FormHelperText>Your official company or organization name</FormHelperText>
                  <FormErrorMessage color={errorColor}>
                    {errors.hiring_company_name && errors.hiring_company_name.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.job_title_uuid}>
                  <FormLabel>Job Title</FormLabel>

                  {/* Keep a hidden field for RHF */}
                  <input type="hidden" {...register("job_title_uuid")} name="job_title_uuid" />

                  <Menu autoSelect={false} closeOnSelect>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      rightIcon={<ChevronDownIcon className="h-5 w-5" />}
                      width="100%"
                      justifyContent="space-between"
                      fontWeight="normal"
                      _focus={{ borderColor: accentColor }}
                      _hover={{ borderColor: accentColor }}
                      borderColor={errors.job_title_uuid ? errorColor : "gray.200"}
                      borderWidth="1px"
                      aria-invalid={!!errors.job_title_uuid}
                      className="text-left"
                    >
                      {selectedJobTitle?.title || "Select a job title"}
                    </MenuButton>
                    <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                      <Input
                        size="sm"
                        placeholder="Type to search job titles..."
                        value={jobTitleKeyword}
                        onChange={(e) => setJobTitleKeyword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault()
                        }}
                        autoFocus
                        _focus={{ borderColor: accentColor }}
                      />
                      <Box maxH="260px" overflowY="auto" mt={2}>
                        {jobTitlesLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text>Loading...</Text>
                          </HStack>
                        ) : jobTitle && jobTitle.length > 0 ? (
                          jobTitle.map((item) => (
                            <MenuItem key={item.uuid} onClick={() => handleJobTitleSelect(item)}>
                              {item.title}
                            </MenuItem>
                          ))
                        ) : (
                          <Text p={3} color="gray.500">
                            No results
                          </Text>
                        )}
                      </Box>
                    </MenuList>
                  </Menu>

                  <FormHelperText>Choose the title that best matches the position</FormHelperText>
                  <FormErrorMessage color={errorColor}>
                    {errors.job_title_uuid && errors.job_title_uuid.message}
                  </FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired isInvalid={!!errors.job_name}>
                  <FormLabel>Designation</FormLabel>
                  <Input
                    {...register("job_name")}
                    placeholder="E.g., Senior Frontend Developer"
                    _focus={{ borderColor: accentColor }}
                    name="job_name"
                  />
                  <FormHelperText>A specific name for this position</FormHelperText>
                  <FormErrorMessage color={errorColor}>{errors.job_name && errors.job_name.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.job_tag}>
                  <FormLabel>Level</FormLabel>
                  <Select
                    {...register("job_tag")}
                    placeholder="Select job level"
                    _focus={{ borderColor: accentColor }}
                    name="job_tag"
                  >
                    <option value="freelance">Freelance</option>
                    <option value="internship">Internship</option>
                    <option value="flexible location">Flexible Location</option>
                    <option value="entry-level">Entry-Level</option>
                    <option value="mid-level">Mid-Level</option>
                    <option value="senior-level">Senior-Level</option>
                    <option value="immediate joiner">Immediate Joiner</option>
                    <option value="flexible hours">Flexible Hours</option>
                  </Select>
                  <FormHelperText>Indicate the experience level required</FormHelperText>
                  <FormErrorMessage color={errorColor}>{errors.job_tag && errors.job_tag.message}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired isInvalid={!!errors.department_uuid}>
                <FormLabel>Department</FormLabel>
                <Select
                  {...register("department_uuid")}
                  placeholder="Select Job Department"
                  _focus={{ borderColor: accentColor }}
                  name="department_uuid"
                >
                  {departments &&
                    departments.map((department) => (
                      <option
                        key={department.uuid}
                        value={JSON.stringify({
                          uuid: department.uuid,
                          title: department.title,
                        })}
                      >
                        {department.title}
                      </option>
                    ))}
                </Select>
                <FormHelperText>Department this position belongs to</FormHelperText>
                <FormErrorMessage color={errorColor}>
                  {errors.department_uuid && errors.department_uuid.message}
                </FormErrorMessage>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <FormControl isRequired isInvalid={!!errors.job_type}>
                  <FormLabel>Job Type</FormLabel>
                  <Select {...register("job_type")} defaultValue="Full Time" _focus={{ borderColor: accentColor }} name="job_type">
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                    <option value="Internship">Internship</option>
                    <option value="Freelance">Freelance</option>
                    <option value="Temporary">Temporary</option>
                  </Select>
                  <FormErrorMessage color={errorColor}>{errors.job_type && errors.job_type.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.work_location_type}>
                  <FormLabel>Location Type</FormLabel>
                  <Select
                    {...register("work_location_type")}
                    defaultValue="On Site"
                    _focus={{ borderColor: accentColor }}
                    name="work_location_type"
                  >
                    <option value="On Site">On Site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </Select>
                  <FormErrorMessage color={errorColor}>
                    {errors.work_location_type && errors.work_location_type.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.job_shift}>
                  <FormLabel>Shift</FormLabel>
                  <Select {...register("job_shift")} defaultValue="Day Shift" _focus={{ borderColor: accentColor }} name="job_shift">
                    <option value="Day Shift">Day Shift</option>
                    <option value="Morning Shift">Morning Shift</option>
                    <option value="Night Shift">Night Shift</option>
                    <option value="Mon - Fri">Mon - Fri</option>
                    <option value="UK Shift">UK Shift</option>
                  </Select>
                  <FormErrorMessage color={errorColor}>{errors.job_shift && errors.job_shift.message}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md">
                <Heading as="h3" size="md" mb={2}>
                  Job Location
                </Heading>
                <Text color="gray.600">Provide address details for this position.</Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired isInvalid={!!errors.address1}>
                  <FormLabel>Address(apartment/floor/building)</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      {...register("address1")}
                      pl={10}
                      placeholder="Street address"
                      _focus={{ borderColor: accentColor }}
                      name="address1"
                    />
                  </InputGroup>
                  <FormErrorMessage color={errorColor}>{errors.address1 && errors.address1.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.address2}>
                  <FormLabel>Street Address</FormLabel>
                  <Input
                    {...register("address2")}
                    placeholder="Apt, Suite, Building (optional)"
                    _focus={{ borderColor: accentColor }}
                    name="address2"
                  />
                  <FormErrorMessage color={errorColor}>{errors.address2 && errors.address2.message}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <FormControl isRequired isInvalid={!!errors.country_uuid}>
                  <FormLabel>Country</FormLabel>
                  <Select
                    {...register("country_uuid")}
                    onChange={countryChange}
                    placeholder="Select country"
                    _focus={{ borderColor: accentColor }}
                    name="country_uuid"
                  >
                    {countryList &&
                      countryList.map((item) => (
                        <option
                          key={item.id || item.uuid}
                          value={JSON.stringify({
                            uuid: item.uuid,
                            title: item.name,
                          })}
                        >
                          {item.name}
                        </option>
                      ))}
                  </Select>
                  <FormErrorMessage color={errorColor}>
                    {errors.country_uuid && errors.country_uuid.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.state_uuid}>
                  <FormLabel>State/Province</FormLabel>
                  <Select
                    {...register("state_uuid", {
                      validate: () => (selectedCountryId ? true : "Please select a country first"),
                    })}
                    onChange={stateChange}
                    placeholder="Select state"
                    isDisabled={!selectedCountryId}
                    _focus={{ borderColor: accentColor }}
                    name="state_uuid"
                  >
                    {stateList &&
                      stateList.map((state) => (
                        <option
                          key={state.uuid}
                          value={JSON.stringify({
                            uuid: state.uuid,
                            title: state.name,
                          })}
                        >
                          {state.name}
                        </option>
                      ))}
                  </Select>
                  {!selectedCountryId && <FormHelperText>Select a country first</FormHelperText>}
                  <FormErrorMessage color={errorColor}>
                    {errors.state_uuid && errors.state_uuid.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.city_uuid}>
                  <FormLabel>City</FormLabel>
                  <Select
                    {...register("city_uuid", {
                      validate: () => (selectedStateId ? true : "Please select a state first"),
                    })}
                    placeholder="Select city"
                    isDisabled={!selectedStateId}
                    _focus={{ borderColor: accentColor }}
                    name="city_uuid"
                  >
                    {Array.isArray(citiesList) &&
                      citiesList.length > 0 &&
                      citiesList.map((cities) => (
                        <option
                          key={cities.id || cities.uuid}
                          value={JSON.stringify({
                            uuid: cities.uuid,
                            title: cities.name,
                          })}
                        >
                          {cities.name}
                        </option>
                      ))}
                  </Select>
                  {!selectedStateId && <FormHelperText>Select a state first</FormHelperText>}
                  <FormErrorMessage color={errorColor}>{errors.city_uuid && errors.city_uuid.message}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired isInvalid={!!errors.pincode}>
                <FormLabel>Postal/Zip Code</FormLabel>
                <Input
                  {...register("pincode", { valueAsNumber: true })}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "")
                    if (value.length > 6) value = value.slice(0, 6)
                    setValue("pincode", value ? Number.parseInt(value, 10) : "", { shouldValidate: true })
                  }}
                  placeholder="Enter 5-6 digit code"
                  _focus={{ borderColor: accentColor }}
                  name="pincode"
                />
                <FormErrorMessage color={errorColor}>{errors.pincode && errors.pincode.message}</FormErrorMessage>
              </FormControl>

              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md">
                <Heading as="h3" size="md" mb={2}>
                  Salary & Benefits Details
                </Heading>
                <Text color="gray.600">Provide salary and benefits information for better candidate matches.</Text>
              </Box>

              {/* Hidden inputs so RHF tracks pay_type and salary_not_disclose */}
              <input type="hidden" {...register("pay_type")} name="pay_type" />
              <input type="hidden" {...register("salary_not_disclose")} name="salary_not_disclose" />

              <FormControl>
                <FormLabel>Pay Type</FormLabel>
                <Select
                  defaultValue="Do Not Disclose"
                  onChange={handleSelectChange}
                  _focus={{ borderColor: accentColor }}
                  name="pay_type_select_only"
                >
                  <option value="Do Not Disclose">Do Not Disclose</option>
                  <option value="Salary Range">Salary Range (Min to Max)</option>
                </Select>
                <FormHelperText>Job postings with salary information get 30% more applications</FormHelperText>
              </FormControl>

              {payByOption === "Salary Range" && (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <FormControl isRequired isInvalid={!!errors.salary_min_amount}>
                    <FormLabel>Minimum Salary</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <CurrencyRupeeIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Input
                        {...register("salary_min_amount")}
                        pl={10}
                        placeholder="e.g., 50000"
                        _focus={{ borderColor: accentColor }}
                        name="salary_min_amount"
                      />
                    </InputGroup>
                    <FormErrorMessage color={errorColor}>
                      {errors.salary_min_amount && errors.salary_min_amount.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.salary_max_amount}>
                    <FormLabel>Maximum Salary</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <CurrencyRupeeIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Input
                        {...register("salary_max_amount")}
                        pl={10}
                        placeholder="e.g., 70000"
                        _focus={{ borderColor: accentColor }}
                        name="salary_max_amount"
                      />
                    </InputGroup>
                    <FormErrorMessage color={errorColor}>
                      {errors.salary_max_amount && errors.salary_max_amount.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.salary_per_type}>
                    <FormLabel>Salary Period</FormLabel>
                    <Select {...register("salary_per_type")} defaultValue="Monthly" _focus={{ borderColor: accentColor }} name="salary_per_type">
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Yearly">Yearly</option>
                    </Select>
                    <FormErrorMessage color={errorColor}>
                      {errors.salary_per_type && errors.salary_per_type.message}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              )}

              <FormControl isInvalid={!!errors.additional_pay}>
                <FormLabel>Additional Benefits</FormLabel>
                <Box position="relative">
                  {/* Hidden input to store CSV of benefits */}
                  <input type="hidden" {...register("additional_pay")} name="additional_pay" />
                  <Wrap mb={2} spacing={2}>
                    {selectedBenefits.map((benefit) => (
                      <WrapItem key={benefit}>
                        <Tag
                          size="md"
                          borderRadius="full"
                          variant="solid"
                          colorScheme="blue"
                        >
                          <TagLabel>{benefit}</TagLabel>
                          <TagCloseButton
                            onClick={() => {
                              const updated = selectedBenefits.filter((b) => b !== benefit)
                              setSelectedBenefits(updated)
                            }}
                          />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>

                  <Select
                    placeholder="e.g., Health Insurance, 401k, Flexible hours"
                    _focus={{ borderColor: accentColor }}
                    onChange={(e) => {
                      const value = e.target.value
                      if (!value) return
                      if (!selectedBenefits.includes(value)) {
                        const updated = Array.from(new Set([...selectedBenefits, value]))
                        setSelectedBenefits(updated)
                      }
                      e.target.value = ""
                    }}
                    name="additional_pay_select_only"
                  >
                    <option value="Competitive Salary">Competitive Salary</option>
                    <option value="Performance Bonus / Incentives">Performance Bonus / Incentives</option>
                    <option value="Profit Sharing">Profit Sharing</option>
                    <option value="Stock Options (ESOPs)">Stock Options (ESOPs)</option>
                    <option value="Retirement Plans (PF, Pension, 401k etc.)">
                      Retirement Plans (PF, Pension, 401k etc.)
                    </option>
                    <option value="Annual Increment / Salary Hike">Annual Increment / Salary Hike</option>
                    <option value="Health Insurance (self + family)">Health Insurance (self + family)</option>
                    <option value="Dental / Vision Insurance">Dental / Vision Insurance</option>
                    <option value="Life Insurance">Life Insurance</option>
                    <option value="Disability Insurance">Disability Insurance</option>
                    <option value="Wellness Programs (yoga, gym, meditation sessions)">
                      Wellness Programs (yoga, gym, meditation sessions)
                    </option>
                    <option value="Mental Health Support">Mental Health Support</option>
                    <option value="Paid Leave / Casual Leave / Sick Leave">
                      Paid Leave / Casual Leave / Sick Leave
                    </option>
                    <option value="Paid Holidays / Public Holidays">Paid Holidays / Public Holidays</option>
                    <option value="Maternity & Paternity Leave">Maternity & Paternity Leave</option>
                    <option value="Marriage Leave / Compassionate Leave">Marriage Leave / Compassionate Leave</option>
                    <option value="Sabbatical Leave">Sabbatical Leave</option>
                    <option value="Work From Home / Flexible Hours">Work From Home / Flexible Hours</option>
                    <option value="Flexible Work Arrangements (Remote / Hybrid work)">
                      Flexible Work Arrangements (Remote / Hybrid work)
                    </option>
                    <option value="Transport Facility / Cab Service">Transport Facility / Cab Service</option>
                    <option value="Meal Coupons / Free Lunch / Snacks">Meal Coupons / Free Lunch / Snacks</option>
                    <option value="Company Laptop / Mobile / Internet Allowance">
                      Company Laptop / Mobile / Internet Allowance
                    </option>
                    <option value="Training & Development Programs">Training & Development Programs</option>
                    <option value="Higher Education Assistance (Tuition Reimbursement)">
                      Higher Education Assistance (Tuition Reimbursement)
                    </option>
                    <option value="Certifications & Skill Development Support">
                      Certifications & Skill Development Support
                    </option>
                    <option value="Internal Promotions & Career Growth Opportunities">
                      Internal Promotions & Career Growth Opportunities
                    </option>
                    <option value="Employee Recognition Awards">Employee Recognition Awards</option>
                    <option value="Gift Vouchers / Perks for Performance">Gift Vouchers / Perks for Performance</option>
                    <option value="Team Building Activities / Outings">Team Building Activities / Outings</option>
                    <option value="Annual Parties & Celebrations">Annual Parties & Celebrations</option>
                    <option value="Festival Gifts">Festival Gifts</option>
                    <option value="Flexible Timings">Flexible Timings</option>
                    <option value="Remote / Hybrid Work Options">Remote / Hybrid Work Options</option>
                    <option value="Childcare Support / Creche Facility">Childcare Support / Creche Facility</option>
                    <option value="Family Support Programs">Family Support Programs</option>
                    <option value="Travel Allowance / Company Car">Travel Allowance / Company Car</option>
                    <option value="Housing Allowance">Housing Allowance</option>
                    <option value="Relocation Assistance">Relocation Assistance</option>
                    <option value="Club Memberships">Club Memberships</option>
                    <option value="Discount Programs (on company products or partner brands)">
                      Discount Programs (on company products or partner brands)
                    </option>
                  </Select>
                </Box>
                <FormHelperText>List perks and benefits to attract qualified candidates</FormHelperText>
                <FormErrorMessage color={errorColor}>
                  {errors.additional_pay && errors.additional_pay.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.joining_amount_required}>
                <FormLabel>Is there any joining amount? (Fees)</FormLabel>
                <RadioGroup
                  onChange={(value) => {
                    const isJoiningAmountRequired = value === "true"
                    setValue("joining_amount_required", isJoiningAmountRequired, { shouldValidate: true })
                    if (!isJoiningAmountRequired) {
                      setValue("joining_amount", "", { shouldValidate: true })
                    }
                    trigger("joining_amount")
                    trigger("joining_amount_required")
                  }}
                  value={watch("joining_amount_required") ? "true" : "false"}
                >
                  <Stack direction="row">
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                  </Stack>
                </RadioGroup>
                <FormHelperText>Indicate if candidates need to pay any joining fees</FormHelperText>
                <FormErrorMessage color={errorColor}>
                  {errors.joining_amount_required && errors.joining_amount_required.message}
                </FormErrorMessage>
              </FormControl>

              {watch("joining_amount_required") && (
                <FormControl mt={0} isInvalid={!!errors.joining_amount}>
                  <FormLabel>Joining Amount</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" color="gray.300" fontSize="1.2em">
                      ₹
                    </InputLeftElement>
                    <Input
                      placeholder="Enter amount"
                      {...register("joining_amount")}
                      type="number"
                      min="0"
                      pl={8}
                      name="joining_amount"
                    />
                  </InputGroup>
                  <FormHelperText>Enter the joining fee amount in numbers only</FormHelperText>
                  <FormErrorMessage color={errorColor}>
                    {errors.joining_amount && errors.joining_amount.message}
                  </FormErrorMessage>
                </FormControl>
              )}

              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md">
                <Heading as="h3" size="md" mb={2}>
                  Job Description
                </Heading>
                <Text color="gray.600">Create a compelling job description to attract the right candidates.</Text>
              </Box>

              <FormControl isInvalid={!!errors.no_of_hire}>
                <FormLabel>Number of Openings</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  </InputLeftElement>
                  <Input
                    {...register("no_of_hire")}
                    placeholder="e.g., 10"
                    pl={10}
                    _focus={{ borderColor: accentColor }}
                    name="no_of_hire"
                  />
                </InputGroup>
                <FormHelperText>How many positions are available for this role</FormHelperText>
                <FormErrorMessage color={errorColor}>{errors.no_of_hire && errors.no_of_hire.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.job_description}>
                <FormLabel>Job Description</FormLabel>
                <Box
                  id="job-description-editor"
                  border="1px solid"
                  borderColor={errors.job_description ? errorColor : "gray.200"}
                  borderRadius="md"
                >
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    placeholder="Describe your job details here. Include responsibilities, qualifications, and what makes this role unique."
                    style={{ height: "250px", marginBottom: "50px" }}
                    value={quillContent}
                    onChange={handleQuillChange}
                  />
                </Box>

                <input type="hidden" {...register("job_description")} name="job_description" />

                {quillContent && (
                  <Box mt={4}>
                    <FormLabel>Preview:</FormLabel>
                    <Box
                      p={4}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      dangerouslySetInnerHTML={{ __html: quillContent }}
                    />
                  </Box>
                )}
                <FormHelperText>
                  Tips: Include key responsibilities, required skills, and company culture aspects
                </FormHelperText>
                <FormErrorMessage color={errorColor}>
                  {errors.job_description && errors.job_description.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" mr={4}>
                  Is there a specific date for the candidate to join?
                </FormLabel>
                <Switch
                  colorScheme="blue"
                  size="lg"
                  isChecked={isFirstSwitchOn}
                  onChange={(e) => {
                    const isChecked = e.target.checked
                    setIsFirstSwitchOn(isChecked)
                    setValue("is_planned_start_date", isChecked, { shouldValidate: true })
                    if (!isChecked) {
                      setValue("plan_start_date", null)
                    } else {
                      setTimeout(() => trigger("plan_start_date"), 0)
                    }
                  }}
                />
              </FormControl>

              {isFirstSwitchOn && (
                <FormControl isRequired={isFirstSwitchOn} isInvalid={!!errors.plan_start_date}>
                  <FormLabel>Joining date</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      type="date"
                      {...register("plan_start_date")}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value)
                        if (!isNaN(selectedDate.getTime())) {
                          const formattedDate = selectedDate.toISOString().split("T")[0]
                          setValue("plan_start_date", formattedDate, { shouldValidate: true })
                        }
                      }}
                      pl={10}
                      _focus={{ borderColor: accentColor }}
                      name="plan_start_date"
                    />
                  </InputGroup>
                  <FormHelperText>When is the candidate expected to join?</FormHelperText>
                  <FormErrorMessage color={errorColor}>
                    {errors.plan_start_date && errors.plan_start_date.message}
                  </FormErrorMessage>
                </FormControl>
              )}

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                rightIcon={<ChevronRightIcon className="h-5 w-5" />}
                mt={4}
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
                onClick={() => {
                  trigger()
                }}
              >
                Continue to Next Step
              </Button>
            </VStack>
          </form>
        </Box>
      </Container>
    </Box>
  )
}

export default JobDetails1